import { useEffect, useState } from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarBlank, Clock, X } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { loadCompany } from "@/lib/company";

const trigger =
  "flex h-12 w-full items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-left text-sm transition-colors duration-150 hover:border-zinc-700";

const toDate = (v) => {
  if (!v) return undefined;
  const d = parse(v, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
};

export const DateField = ({
  value,
  onChange,
  testId,
  placeholder = "Pick a date",
}) => {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={trigger}>
          <CalendarBlank
            size={16}
            weight="bold"
            className="shrink-0 text-yellow-500"
          />
          <span className={selected ? "font-semibold" : "text-zinc-500"}>
            {selected ? format(selected, "EEE, MMM d yyyy") : placeholder}
          </span>
          {selected && (
            <span
              data-testid={`${testId}-clear`}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setOpen(false);
              }}
              className="ml-auto text-zinc-500 hover:text-white"
            >
              <X size={14} weight="bold" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto border-zinc-800 bg-zinc-900 p-0"
      >
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => {
            onChange(d ? format(d, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

const SLOTS = [];
for (let h = 6; h <= 19; h += 1) {
  ["00", "30"].forEach((m) => SLOTS.push(`${String(h).padStart(2, "0")}:${m}`));
}

// Business hours from Settings → My Company, widened by an hour either side.
const slotsFor = (hours) => {
  const parseHour = (v, fallback) => {
    const n = parseInt(String(v || "").slice(0, 2), 10);
    return Number.isFinite(n) ? n : fallback;
  };
  const start = Math.max(0, parseHour(hours?.start, 8) - 1);
  const end = Math.min(23, parseHour(hours?.end, 17) + 1);
  if (end <= start) return SLOTS;
  const out = [];
  for (let h = start; h <= end; h += 1) {
    ["00", "30"].forEach((m) => out.push(`${String(h).padStart(2, "0")}:${m}`));
  }
  return out;
};

const pretty = (t) => {
  const d = parse(t, "HH:mm", new Date());
  return isValid(d) ? format(d, "h:mm a") : t;
};

export const TimeField = ({
  value,
  onChange,
  testId,
  placeholder = "Pick a time",
}) => {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState(SLOTS);

  useEffect(() => {
    loadCompany().then((conf) => setSlots(slotsFor(conf?.hours)));
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={trigger}>
          <Clock size={16} weight="bold" className="shrink-0 text-yellow-500" />
          <span className={value ? "font-semibold" : "text-zinc-500"}>
            {value ? pretty(value) : placeholder}
          </span>
          {value && (
            <span
              data-testid={`${testId}-clear`}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setOpen(false);
              }}
              className="ml-auto text-zinc-500 hover:text-white"
            >
              <X size={14} weight="bold" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-44 border-zinc-800 bg-zinc-900 p-1"
      >
        <div className="max-h-64 overflow-y-auto">
          {slots.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`${testId}-slot-${t}`}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
              className={`block w-full rounded px-3 py-2 text-left text-sm transition-colors duration-150 ${
                value === t
                  ? "bg-indigo-500 font-bold text-white"
                  : "text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              {pretty(t)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
